/**
 * @file: The export text of the play report.
 *
 * A sponsor reads the export in Excel, and Danish Excel opens a semicolon file
 * without an import dialog. The component adds the byte order mark and the
 * download; this file only turns rows into text, so a spec reads the text.
 *
 * One line per media, scene and festival day. That is the shape the server
 * sends, so the export holds every number the page adds together.
 */

import { formatClock } from 'common/clock'

/** Excel reads a semicolon file, and a comma is in many media names. */
const SEPARATOR = ';'

/** Excel expects the line ends of a Windows file. */
const LINE_END = '\r\n'

const COLUMNS = Object.freeze([
  'day', 'scene', 'type', 'media', 'plays', 'ended', 'failed', 'unended', 'screenTime', 'screenMs'
])

/**
 * A spreadsheet runs a field that starts with one of these characters as a
 * formula. A media name comes from the library and can start with any of them.
 * An apostrophe in front of the text makes the cell text again.
 */
const FORMULA_START = /^[=+\-@\t\r]/

const quote = text => `"${text.replace(/"/g, '""')}"`

/** A field that holds a separator, a quote or a line end is quoted. */
function field (value) {
  const text = value == null ? '' : String(value)

  if (FORMULA_START.test(text)) {
    return quote(`'${text}`)
  }
  return /[;"\r\n]/.test(text) ? quote(text) : text
}

const line = values => values.map(field).join(SEPARATOR)

/**
 * The export text of the report, with the header line in front of the rows.
 *
 * @param {Array} rows - `{ day, scene, type, media, plays, ended, failed, unended, screenMs }`
 * @returns {string} The text of the file, without the byte order mark
 */
function csvText (rows) {
  const body = (rows || []).map(row => line([
    row.day, row.scene, row.type, row.media, row.plays, row.ended, row.failed, row.unended,
    formatClock(row.screenMs), row.screenMs
  ]))

  return [line(COLUMNS), ...body].join(LINE_END)
}

export { csvText }
